import { useState, useEffect, useCallback, useRef } from 'react';

type Payload = {
    page: number;
    modalPhotoIndex: number;
    itemsOnPage: number;
    totalResults: number;
    currentPhotoIndex: number;
    changePhotoIndex: ({ direction }: { direction: 'prev' | 'next' }) => void;
};

type ReturnValue = {
    swiperKey: number;
    startingPage: number;
    onPageChange: (page: number, prevPage: number) => void;
};

export function usePhotoSwiperState({
    page,
    modalPhotoIndex,
    itemsOnPage,
    totalResults,
    currentPhotoIndex,
    changePhotoIndex,
}: Payload): ReturnValue {
    const [startingPage, setStartingPage] = useState(modalPhotoIndex + 1);
    const [swiperKey, setSwiperKey] = useState(new Date().getTime());

    const pageRef = useRef(page);
    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const onPageChange = useCallback(
        (swiperPage: number, prevPage: number) => {
            /**
             * Skip swiper page change when swiping back:
             * - from left span placeholder to 1st photo
             * - from last span placeholder to last photo
             */
            const pageRefCurrent = pageRef.current || 1;
            const isLeftPlaceholder = prevPage === 0;
            const pageItemsCount = (pageRefCurrent - 1) * itemsOnPage;
            const isRightPlaceholder = pageItemsCount + prevPage === totalResults + 1;

            if (isLeftPlaceholder || isRightPlaceholder) {
                return;
            }

            setStartingPage(swiperPage);
            const swiperImageIndex = pageItemsCount + swiperPage;
            if ((pageRefCurrent === 1 && swiperPage === 0) || swiperImageIndex > totalResults) {
                /**
                 * Return when swiping:
                 * - left on first page first photo
                 * - right on last page last photo
                 */
                return;
            }

            if (swiperPage < prevPage) {
                changePhotoIndex({ direction: 'prev' });
            } else if (swiperPage > prevPage) {
                changePhotoIndex({ direction: 'next' });
            }
        },
        [setStartingPage, changePhotoIndex, itemsOnPage, totalResults]
    );

    useEffect(() => {
        setStartingPage(modalPhotoIndex + 1);
    }, [modalPhotoIndex]);

    useEffect(() => {
        /**
         * When very first or last photo is being swiped to the right/left we want swiper to be restored
         * to show that first or last photo instead of empty placeholder used to change facet filter pages
         */
        const pageRefCurrent = pageRef.current || 1;
        const pageItemsCount = (pageRefCurrent - 1) * itemsOnPage;
        if (pageRefCurrent === 1 && startingPage === 0 && currentPhotoIndex === 0) {
            setStartingPage(1);
        } else if (pageItemsCount + startingPage > totalResults) {
            setStartingPage(startingPage - 1);
        }
    }, [startingPage, currentPhotoIndex, totalResults, itemsOnPage]);

    useEffect(() => {
        setSwiperKey(new Date().getTime());
    }, [onPageChange]);

    return { swiperKey, startingPage, onPageChange };
}
